import { useMemo, useRef } from "react";
import { IExpressionEvaluatorContext } from "../../ExpressionsModule/Models/IExpressionEvaluatorContext";
import { ObjectVariableScope } from "../../ExpressionsModule/Runtime/VariableScopes/ObjectVariableScope";

/**
 * Result of creating child contexts for a collection of items.
 */
export interface ChildContextItem<TItem = any> {
    /** Unique key for this item (from keyField or index) */
    key: string | number;
    /** The original item data */
    item: TItem;
    /** Cached child context with item variable set */
    context: any;
}

/**
 * Creates and caches child contexts for a collection of items.
 * Each item gets its own child context with a unique variable.
 * 
 * Optimizes performance by:
 * - Reusing context instances when the same item reference appears again (by key + reference equality)
 * - Creating fresh contexts when an item's reference changes (new API response object)
 * - Cleaning up stale contexts (items no longer in array)
 * - Discarding all cached contexts when the parent scope is replaced (navigation / full refresh)
 * 
 * **Use this hook for:**
 * - Collections where each item needs its own context (e.g., RepeatContainer)
 * - Rendering lists with per-item variables (e.g., $item)
 * 
 * **Don't use this hook for:**
 * - Single child context shared by all children
 *   → Use `useChildContext` instead (see GlobalSearchComponent)
 * 
 * @param parentContext - The parent context to create children from
 * @param items - Array of items to create contexts for
 * @param keyField - Field name to use as unique key (defaults to index if not found)
 * @param variableName - Name of the variable to set in each context (e.g., "$item")
 * @returns Array of context items with keys and contexts
 * 
 * @example
 * // Basic usage with $item
 * const contextItems = useChildContexts(
 *   context, 
 *   items, 
 *   "id", 
 *   VariableNames.Item
 * );
 * 
 * return (
 *   <div>
 *     {contextItems?.map(({ key, context: itemContext }) => (
 *       <React.Fragment key={key}>
 *         {component.Children.map(child => (
 *           <ComponentFactory component={child} context={itemContext} />
 *         ))}
 *       </React.Fragment>
 *     ))}
 *   </div>
 * );
 * 
 * @example
 * // With custom key field
 * const contextItems = useChildContexts(
 *   context,
 *   customers,
 *   "customerId",
 *   VariableNames.Item
 * );
 */
export function useChildContexts<TItem = any>(
    parentContext: IExpressionEvaluatorContext,
    items: TItem[] | null | undefined,
    keyField: string,
    variableName: string
): ChildContextItem<TItem>[] | null {
    // Cache contexts by item key — stores both context and the item reference it was created for.
    const contextCacheRef = useRef<Map<string | number, { context: any; item: TItem }>>(new Map());
    const prevParentScopeRef = useRef<IExpressionEvaluatorContext["VariableScope"] | null>(null);

    const contextItems = useMemo(() => {
        if (!items || items.length === 0) {
            contextCacheRef.current = new Map();
            prevParentScopeRef.current = null;
            return null;
        }

        // If the parent scope itself was replaced (navigation, full refresh), discard all cached
        // child contexts — they hold a stale parent reference and cannot see the new parent's variables.
        if (prevParentScopeRef.current !== parentContext.VariableScope) {
            contextCacheRef.current = new Map();
            prevParentScopeRef.current = parentContext.VariableScope;
        }

        const prevCache = contextCacheRef.current;
        const newCache = new Map<string | number, { context: any; item: TItem }>();

        const result = items.map((item, index) => {
            const itemKey = (item as any)[keyField] ?? index;

            const cached = prevCache.get(itemKey);
            let itemContext: any;

            if (cached && cached.item === item) {
                // Exact same item reference — reuse context without any changes.
                // No set() needed: scope already contains this exact object.
                itemContext = cached.context;
            } else {
                // New item or different item reference — create a fresh child context.
                // This causes the component to remount (new StableId) and re-evaluate all
                // expressions with the correct data. Avoids side effects inside useMemo
                // and avoids the deepEqual-suppressed-notification problem.
                const itemVariableScope = new ObjectVariableScope({ [variableName]: item }, parentContext.VariableScope);
                itemContext = parentContext.CreateChild(itemVariableScope);
            }

            newCache.set(itemKey, { context: itemContext, item });

            return {
                key: itemKey,
                item: item,
                context: itemContext,
            };
        });

        // Replace cache — stale keys (items no longer in array) are implicitly pruned.
        contextCacheRef.current = newCache;

        return result;
        // parentContext.VariableScope (object ref) changes only when the scope is fully replaced
        // (navigation, full refresh). When individual variables change within the same scope,
        // VariableScope stays the same reference, so this memo does not re-run — item components
        // observe their own variables via the observer pattern and update without remounting.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentContext.VariableScope, items, keyField, variableName]);

    return contextItems;
}
